import { Column, Section } from '@miq/catalog';
import { DataObject } from '@shared/models/data-object.model';
import { LocaleId } from '@shared/models/language.model';
import moment from 'moment';

export const UTC_EDP_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
export const UTC_EDP_OUTPUT_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]';

export interface LeaseTermParams {
  leaseTermColumn: Column;
  startDateColumn: Column;
  endDateColumn: Column;
}

export const uomCorrect = (locale: LocaleId, uomValue: string) =>
  (locale === LocaleId.US && uomValue === 'Months') ||
  ([LocaleId.UkEnglish, LocaleId.NlEnglish].includes(locale) && uomValue === 'Years');
export function getEndDateCalculationFunction(params: LeaseTermParams, locale: LocaleId) {
  /**
   * Conditions for End date to be calculated
   * 1.- Lease term has a value greater than 0
   * 2.- If locale is US, uom should be months, If UK or NL, uom should be years
   * 3.- Start date has a valid value
   */
  const uomValue = params.leaseTermColumn.unit.value;
  const uomIsCorrect = uomCorrect(locale, uomValue);
  let resultFunction;
  if (params.leaseTermColumn.value > 0 && uomIsCorrect && params.startDateColumn.value != null) {
    const calculationType = locale === LocaleId.US ? 'months' : 'years';
    resultFunction = calculateEndDateBasedOn(calculationType);
  }
  return resultFunction;
}

export function getStartDateCalculationFunction(params: LeaseTermParams, locale: LocaleId) {
  /**
   * Conditions for Start date to be calculated
   * 1.- Lease term has a value greater than 0
   * 2.- If locale is US, uom should be months, If UK or NL, uom should be years
   * 3.- End date has a valid value
   */
  const uomValue = params.leaseTermColumn.unit.value;
  const uomIsCorrect = uomCorrect(locale, uomValue);
  let resultFunction;
  if (params.leaseTermColumn.value > 0 && uomIsCorrect && params.endDateColumn.value != null) {
    const calculationType = locale === LocaleId.US ? 'months' : 'years';
    resultFunction = calculateStartDateBasedOn(calculationType);
  }
  return resultFunction;
}

export function getTermCalculationFunction(params: LeaseTermParams, locale: LocaleId, reportLeaseTerm = false) {
  /**
   * Conditions for term to be calculated
   * 1.- Start date has a valid value
   * 2.- End date has a valid value
   * 3.- End date is greater than Start date
   * 4.- If locale is US, calculate monthly based. If UK or NL, calculate yearly based
   */
  let resultFunction;
  if (params.startDateColumn.value != null && params.endDateColumn.value != null) {
    const endDate = moment(params.endDateColumn.value, UTC_EDP_INPUT_FORMAT).utc();
    const startDate = moment(params.startDateColumn.value, UTC_EDP_INPUT_FORMAT).utc();
    if (endDate.isAfter(startDate) && !reportLeaseTerm) {
      resultFunction = locale === LocaleId.US ? calculateLeaseTerm_MonthlyBased : calculateLeaseTerm_YearlyBased;
    } else if (endDate.isAfter(startDate) && reportLeaseTerm) {
      resultFunction =
        locale === LocaleId.US
          ? calculateLeaseTerm_MonthlyBased(params, {})
          : calculateLeaseTerm_YearlyBased(params, {});
    }
  }
  return resultFunction;
}

export function getParams(section: Pick<Section, 'columns'>): LeaseTermParams {
  return {
    leaseTermColumn: section.columns.find(x => x.columnId === 1636),
    startDateColumn: section.columns.find(x => x.columnId === 1633),
    endDateColumn: section.columns.find(x => x.columnId === 1635),
  };
}

export function calculateLeaseTermMonthly(startDate: string, endDate: string) {
  const stdEndDate = moment(endDate, UTC_EDP_INPUT_FORMAT).utc();
  const stdStartDate = moment(startDate, UTC_EDP_INPUT_FORMAT).utc();
  // Get the difference in months, this will return a float value
  const term = stdEndDate.diff(stdStartDate, 'months', true);
  // Remove the decimals to get the months only
  let monthsDifference = Math.trunc(term);
  // Get the difference in days between the startDate plus the months and the end date
  const daysDifference = stdEndDate.diff(stdStartDate.add(monthsDifference, 'months'), 'days', true);
  // Add one month when the difference in days is 15 or more
  monthsDifference = daysDifference > 14 ? monthsDifference + 1 : monthsDifference;
  return monthsDifference;
}

function calculateLeaseTerm_MonthlyBased(params: LeaseTermParams, context: DataObject): Column[] {
  const modifiedColumns = [];
  const monthsDifference = calculateLeaseTermMonthly(params.startDateColumn.value, params.endDateColumn.value);
  params.leaseTermColumn.value = monthsDifference + '';
  params.leaseTermColumn.unit.value = 'Months';
  modifiedColumns.push(params.leaseTermColumn);
  modifiedColumns.push(params.leaseTermColumn.unit);
  context[params.leaseTermColumn.columnId] = true;
  return modifiedColumns;
}

export function calculateLeaseTermYearly(startDate: string, endDate: string): number {
  const stdEndDate = moment(endDate, UTC_EDP_INPUT_FORMAT).utc();
  const stdStartDate = moment(startDate, UTC_EDP_INPUT_FORMAT).utc();
  // Get the difference in years
  const term = stdEndDate.diff(stdStartDate, 'years', true);
  // Remove the decimals
  let yearsDifference = Math.trunc(term);
  // If it's less than 1 year, populate with 0 years
  if (yearsDifference > 0) {
    // Get the difference in months between the startDate plus the years and the end date
    const monthsDifference = stdEndDate.diff(stdStartDate.add(yearsDifference, 'years'), 'months', true);
    // Round down if it's a year + less than 6 months and round up if it's a year with 6 months or more.
    yearsDifference = monthsDifference >= 6 ? yearsDifference + 1 : yearsDifference;
  }
  return yearsDifference;
}

function calculateLeaseTerm_YearlyBased(params: LeaseTermParams, context: DataObject): Column[] {
  const modifiedColumns = [];
  const yearsDifference = calculateLeaseTermYearly(params.startDateColumn.value, params.endDateColumn.value);
  params.leaseTermColumn.value = yearsDifference + '';
  params.leaseTermColumn.unit.value = 'Years';
  modifiedColumns.push(params.leaseTermColumn);
  modifiedColumns.push(params.leaseTermColumn.unit);
  context[params.leaseTermColumn.columnId] = true;
  return modifiedColumns;
}

export function calculateEndDateBasedOn(type: 'months' | 'years') {
  return function calculateEndDate(params: LeaseTermParams, context: DataObject): Column[] {
    const modifiedColumns = [];
    const endDate = moment(params.startDateColumn.value, UTC_EDP_INPUT_FORMAT)
      .utc()
      .add(params.leaseTermColumn.value, type)
      .subtract(1, 'days')
      .format(UTC_EDP_OUTPUT_FORMAT);
    params.endDateColumn.value = endDate;
    modifiedColumns.push(params.endDateColumn);
    context[params.endDateColumn.columnId] = true;
    return modifiedColumns;
  };
}

function calculateStartDateBasedOn(type: 'months' | 'years') {
  return function calculateStartDate(params: LeaseTermParams, context: DataObject): Column[] {
    const modifiedColumns = [];
    const startDate = moment(params.endDateColumn.value, UTC_EDP_INPUT_FORMAT)
      .utc()
      .subtract(params.leaseTermColumn.value, type)
      .add(1, 'days')
      .format(UTC_EDP_OUTPUT_FORMAT);
    params.startDateColumn.value = startDate;
    modifiedColumns.push(params.startDateColumn);
    context[params.startDateColumn.columnId] = true;
    return modifiedColumns;
  };
}
